/**
 * AssignmentBadgesContext — minimal poll/cache of `/api/learning/assignments/badges`
 * for the navbar and the Devoirs page. Keeps the call shape consistent so
 * any component can call `useAssignmentBadges()` and trigger `refresh()`
 * after a mark-seen / submit action.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import { learningApi } from '../services/api';
import { useAuth } from './AuthContext';

const Ctx = createContext({
  receivedUnseenCount: 0,
  submittedUnseenCount: 0,
  byAssignment: [],
  refresh: () => {},
});

const POLL_MS = 60_000; // light polling; mark-seen always refreshes immediately

export const AssignmentBadgesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState({
    receivedUnseenCount: 0,
    submittedUnseenCount: 0,
    byAssignment: [],
  });

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setState({ receivedUnseenCount: 0, submittedUnseenCount: 0, byAssignment: [] });
      return;
    }
    try {
      const r = await learningApi.badges();
      setState({
        receivedUnseenCount: r.data?.receivedUnseenCount || 0,
        submittedUnseenCount: r.data?.submittedUnseenCount || 0,
        byAssignment: r.data?.byAssignment || [],
      });
    } catch {
      // ignore — keep last known state
    }
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const t = setInterval(refresh, POLL_MS);
    // Also refresh whenever the tab regains focus / becomes visible so the
    // badge can't lag behind a mark-seen action that happened in another
    // tab or after the OS-level minimisation of the browser. (Lot 33)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, refresh]);

  return (
    <Ctx.Provider value={{ ...state, refresh }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAssignmentBadges = () => useContext(Ctx);
